// Él-zárás (élzárás) mező- és token-definíciók.
//
// Külön (leaf) modulban tartjuk, hogy NE keletkezzen körkörös import a DecorCardContent ↔
// EdgeListContent között — különben az EdgeListContent modul-szintű olvasása (pl.
// ALL_EDGE_KEYS) "Cannot access 'ALL_EDGE_KEYS' before initialization" TDZ hibát dobna.

// Oldal-alapú él-mezők (a 4 fizikai oldal).
export const SIDE_KEYS = ["TopEdgeProduct", "BottomEdgeProduct", "LeftEdgeProduct", "RightEdgeProduct"] as const;
// Láthatóság-alapú él-mezők: látható / takart / rejtett.
export const VISIBILITY_KEYS = ["VisibleEdgeProduct", "JointEdgeProduct", "HiddenEdgeProduct"] as const;
// Az ALAPÉRTELMEZETT él-cél: a LÁTHATÓ él. Üres kártyán ide kerül a gyári él — nem a négy oldalra.
export const VISIBLE_EDGE_KEY = "VisibleEdgeProduct";
// Minden él-mező. A generikus él-műveletek (méretszűrés, dekor-csere, törlés) MINDET kezelik;
// csak a tényleg oldal-pozíció-függő logika (pl. szabad-oldal kiosztás, avatar keret) marad SIDE_KEYS-en.
export const ALL_EDGE_KEYS = [...SIDE_KEYS, ...VISIBILITY_KEYS] as const;

// Selection-token ↔ paramValue-mező. A két token-halmaz DISZJUNKT, így egy él-bejegyzés
// tokenjei homogének (vagy mind oldal, vagy mind láthatóság) — ez a "kevert nem lehet" szabály alapja.
export const SIDE_TOKENS = ["top", "bottom", "left", "right"] as const;
export const VISIBILITY_TOKENS = ["visible", "joint", "hidden"] as const;
export const TOKEN_TO_FIELD: Record<string, string> = {
    top: "TopEdgeProduct", bottom: "BottomEdgeProduct", left: "LeftEdgeProduct", right: "RightEdgeProduct",
    visible: "VisibleEdgeProduct", joint: "JointEdgeProduct", hidden: "HiddenEdgeProduct",
};

export const ZERO_GUID = "00000000-0000-0000-0000-000000000000";

/**
 * A boardhoz engedélyezett oldal-tokenek (a 4 fizikai oldalból).
 * Worktop (munkalap) boardnál a Top ("front") él NEM választható — csakis worktopnál.
 * Egyetlen forrás: board.IsWorkTop (ugyanaz a flag, amit a maxEdgeWidth is használ).
 * Nem-worktopnál a teljes SIDE_TOKENS jön vissza (változatlan viselkedés).
 */
export function allowedSideTokens(isWorkTop: boolean): readonly string[] {
    return isWorkTop ? SIDE_TOKENS.filter(t => t !== "top") : SIDE_TOKENS;
}

/**
 * A megjeleníthető él-oldal-gombok — DESCRIPTOR-vezérelt. Ha a Decor-descriptor megad `sideLabels`-t,
 * akkor CSAK azok az oldalak látszanak, amikhez van címke (így a descriptorból dönthető el, mely oldal
 * jelenik meg — pl. worktopnál a "right"/front oldalhoz nincs címke → nem látszik). Ha nincs sideLabels,
 * a régi `isWorkTop`-alapú szabályra esünk vissza (allowedSideTokens) — nem-worktop változatlan.
 */
export function shownSideTokens(sideLabels: any, isWorkTop: boolean): readonly string[] {
    if (sideLabels && typeof sideLabels === "object") {
        return SIDE_TOKENS.filter(t => sideLabels[t] !== undefined);
    }
    return allowedSideTokens(isWorkTop);
}

/**
 * A megjeleníthető LÁTHATÓSÁG-gombok — ugyanaz a descriptor-vezérelt szabály, mint az oldalaknál
 * (`shownSideTokens`), csak a másik token-halmazra: CSAK az a gomb látszik, amihez a descriptor
 * `visibilityLabels`-e címkét ad.
 *
 * MIÉRT KELL: eddig a `visibilityLabels` all-or-nothing kapcsoló volt — vagy mind a három gomb (Látható /
 * Kötés / Rejtett) megjelent, vagy egy sem, mert az `EdgeContent` fixen a teljes `VISIBILITY_TOKENS`-en
 * ment végig. Vannak viszont lapok, ahol CSAK a látható él értelmezett (pl. a polc: vékonyan élzárt,
 * kötés- és rejtett éle nincs).
 *
 * A `visibilityLabels` HIÁNYA továbbra is azt jelenti, hogy a csoport egyáltalán nem jelenik meg —
 * ezért ilyenkor üres lista. A ma élő descriptorok mindhárom kulcsot deklarálják, tehát rájuk a szűrő
 * mindhármat visszaadja: NULLA látható változás.
 */
export function shownVisibilityTokens(visibilityLabels: any): readonly string[] {
    if (!visibilityLabels || typeof visibilityLabels !== "object") return [];
    return VISIBILITY_TOKENS.filter(t => visibilityLabels[t] !== undefined);
}

/**
 * A NEM értelmezett oldalak jelzése a thumbnailen: minden oldal, amit az adott lapnál eleve nem lehet
 * élzárni (mert nincs hozzá címke → nincs gombja sem), így a "nincs kiválasztva" és a "nem is választható"
 * állapot nem mosódik össze. Munkalapnál ez a felhasználó felé néző (front) él: ott nincs Top.
 * Nem-worktopnál mind a 4 oldal választható → minden flag false, tehát semmi nem változik.
 */
export function unavailableSideFlags(shownSides: readonly string[]): { top: boolean; bottom: boolean; left: boolean; right: boolean } {
    // ÜRES oldal-lista = a kártya nem oldal-módban dolgozik (pl. a polc, ahol csak a látható él van),
    // NEM pedig "mind a négy oldal tiltott". Ilyenkor egyetlen szaggatott keret sem kell — a jelölés
    // arra való, hogy egy VÁLASZTHATÓ és egy TILTOTT oldal megkülönböztethető legyen, itt viszont
    // nincs mihez képest. Ma egyetlen descriptor sem ad üres listát (a munkalap 4-ből 3-at mutat),
    // tehát ez a mai megjelenésen nem változtat.
    if (!shownSides.length) return { top: false, bottom: false, left: false, right: false };

    return {
        top: !shownSides.includes("top"),
        bottom: !shownSides.includes("bottom"),
        left: !shownSides.includes("left"),
        right: !shownSides.includes("right"),
    };
}

/**
 * A Unity-nak küldött Decor-objektum él-mezőit a kívánt KIMENETI formára alakítja.
 *
 * CSOPORT-alapú szabály (nem mezőnként):
 *  1) Egy csoport AKTÍV, ha legalább egy tagja nem-ZERO. Az aktív csoport MINDEN tagja kimegy
 *     (a be nem állított tagok ZERO_GUID-ként) — így a Unity a teljes csoportot megkapja.
 *  2) A NEM aktív csoport egyetlen mezője sem megy ki → nincs ütközés (pl. nem megy ki egy
 *     RightEdgeProduct=ZERO ami lenullázná a "látszó" oldalt, ha az egyben a jobb oldal is).
 *  3) Ha EGYIK csoport sincs aktív (minden ki van kapcsolva), egyetlen `EdgeProduct = ZERO_GUID`
 *     megy ki, ami mindent nulláz.
 *
 * Ha a Decor-nak egyáltalán nincs él-mezője (pl. nem élzárható lap), érintetlenül hagyjuk.
 */
export function buildDecorEdgeOutput(decor: Record<string, any>): Record<string, any> {
    const hasEdgeFields = ALL_EDGE_KEYS.some(f => decor[f] !== undefined);
    if (!hasEdgeFields) return decor;

    const out: Record<string, any> = {};
    // Nem-él mezők (board, duplung, stb.) elöl — a régi/master EdgeProduct-ot itt kihagyjuk.
    for (const key of Object.keys(decor)) {
        if (key === "EdgeProduct") continue;
        if ((ALL_EDGE_KEYS as readonly string[]).includes(key)) continue;
        out[key] = decor[key];
    }

    const isSet = (f: string) => { const v = decor[f]; return !!v && v !== ZERO_GUID; };
    const sideActive = SIDE_KEYS.some(isSet);
    const visActive = VISIBILITY_KEYS.some(isSet);

    // Az aktív csoport(ok) MINDEN tagja kimegy (a be nem állítottak ZERO_GUID-ként).
    if (sideActive) for (const f of SIDE_KEYS) out[f] = decor[f] ?? ZERO_GUID;
    if (visActive) for (const f of VISIBILITY_KEYS) out[f] = decor[f] ?? ZERO_GUID;

    // Ha egyik csoport sincs aktív: egyetlen EdgeProduct=ZERO megy ki.
    if (!sideActive && !visActive) out.EdgeProduct = ZERO_GUID;

    return out;
}

/**
 * Egy paraméter-wrapper objektum (pl. `{ Decor: {...}, TargetType, Path }`, vagy indexelt
 * `{ "Decor[0]": {...} }`) Decor-szerű al-objektumait átalakítja a Unity-kimeneti formára
 * (buildDecorEdgeOutput). A nem-objektum mezőket (TargetType, Path, stb.) érintetlenül hagyja.
 * Mind a Unity SetStyleScript, mind a kosár-descriptor (generateParameterScripts) ezt használja,
 * hogy a kimenet konzisztensen a csoport-alapú szabályt kövesse.
 */
export function cleanParamEdgeOutput(param: Record<string, any>): Record<string, any> {
    if (!param || typeof param !== "object") return param;
    const out = { ...param };
    for (const prop of Object.keys(out)) {
        const v = out[prop];
        if (v && typeof v === "object" && !Array.isArray(v)) {
            out[prop] = buildDecorEdgeOutput(v);
        }
    }
    return out;
}
